import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const SIGNALMENTS_KEY = 'sentinelle_signalements_frontend';

const http = axios.create({
  baseURL: API_BASE,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' },
});

// Injecter le token JWT automatiquement s'il existe
http.interceptors.request.use((config) => {
  const token = localStorage.getItem('sentinelle_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// --- Helpers ---

function readLocalSignalements() {
  try { return JSON.parse(localStorage.getItem(SIGNALMENTS_KEY) || '[]'); }
  catch { return []; }
}

function writeLocalSignalements(items) {
  localStorage.setItem(SIGNALMENTS_KEY, JSON.stringify(items));
}

function makeReference(prefix = 'SENT') {
  return `${prefix}-${Math.floor(1000 + Math.random() * 9000)}-${Date.now().toString(36).slice(-2).toUpperCase()}`;
}

// --- Transformers : backend → frontend ---

function buildReasons(sources, verdict = 'suspect') {
  const reasons = [];
  if (!sources) return [{ title: 'Vérification terminée', desc: 'Nous avons vérifié ce lien pour vous.' }];

  // Déterminer si des attaques techniques sont détectées dans le lien
  const hasAttack = sources.url_pattern?.has_attack_patterns;
  const hasPhishing = sources.url_pattern?.has_phishing_indicators;
  const isTechnicallyDangerous = hasAttack || hasPhishing;

  // Déterminer si le CONTENU est frauduleux (faux recrutement, fausse promo, deepfake...)
  const hasUnverifiedClaims = sources.claim_verification?.unverified_claims > 0;
  const isContentFraudulent = sources.content_legitimacy?.legitimacy === 'fraudulent';
  const isContentDangerous = hasUnverifiedClaims || isContentFraudulent;

  // Les raisons "safe" (✅) ne s'affichent QUE si TOUT est propre
  const isDangerous = isTechnicallyDangerous || isContentDangerous;

  // URL Pattern Analysis (attaques directes dans le lien)
  if (sources.url_pattern?.success && sources.url_pattern.findings?.length > 0) {
    const findingMessages = {
      xss: { title: '🚨 Ce lien contient un piège caché', desc: 'Ce lien contient du code dangereux qui peut s\'exécuter sur votre téléphone ou ordinateur pour voler vos informations.' },
      sqli: { title: '🚨 Ce lien tente de pirater un site', desc: 'Ce lien essaie de forcer l\'entrée dans la base de données d\'un site pour voler des informations.' },
      command_injection: { title: '🚨 Ce lien tente de prendre le contrôle', desc: 'Ce lien contient des instructions qui essaient de prendre le contrôle d\'un système.' },
      path_traversal: { title: '🚨 Ce lien essaie d\'accéder à des données cachées', desc: 'Ce lien tente d\'accéder à des fichiers secrets qui ne devraient pas être visibles.' },
      open_redirect: { title: '⚠️ Ce lien vous envoie ailleurs en secret', desc: 'Ce lien fait semblant d\'être un site de confiance mais vous envoie secrètement vers un autre site.' },
      typosquatting: { title: '🚨 Ce site imite un site connu', desc: sources.url_pattern.findings.find(f => f.type === 'typosquatting')?.detail || 'Ce site se fait passer pour un site de confiance. C\'est un faux.' },
      suspicious_tld: { title: '⚠️ L\'adresse de ce site est suspecte', desc: sources.url_pattern.findings.find(f => f.type === 'suspicious_tld')?.detail || 'Ce site utilise une extension souvent utilisée par les arnaqueurs.' },
      suspicious_keywords: { title: '⚠️ Ce lien promet des choses trop belles', desc: 'Ce lien contient des mots comme "prix", "gratuit" ou "urgent" souvent utilisés par les arnaqueurs.' },
      encoding: { title: '⚠️ Ce lien cache son vrai contenu', desc: 'Ce lien utilise des codes spéciaux pour cacher ce qu\'il fait vraiment.' },
      suspicious_length: { title: '⚠️ Ce lien est anormalement long', desc: 'Les liens très longs servent souvent à cacher du contenu dangereux.' },
      suspicious_subdomains: { title: '⚠️ L\'adresse de ce site est très compliquée', desc: 'Les vrais sites ont des adresses simples. Une adresse très compliquée est souvent le signe d\'une arnaque.' },
      ip_address: { title: '⚠️ Ce site n\'a pas de vrai nom', desc: 'Ce site utilise un numéro au lieu d\'un nom. Les vrais sites comme Facebook ou Google ont un nom facile à retenir.' },
      no_https: { title: '⚠️ Ce site n\'est pas protégé', desc: 'Ce site n\'utilise pas de protection. Si vous entrez vos informations, quelqu\'un pourrait les intercepter.' },
    };

    const seenTypes = new Set();
    for (const finding of sources.url_pattern.findings) {
      if (seenTypes.has(finding.type)) continue;
      seenTypes.add(finding.type);
      const msg = findingMessages[finding.type];
      if (msg) {
        reasons.push({ title: msg.title, desc: msg.desc });
      }
    }
  } else if (sources.url_pattern?.success) {
    if (isContentDangerous && verdict !== 'safe') {
      // Le lien est techniquement propre MAIS le contenu est frauduleux
      reasons.push({
        title: '⚠️ Ce lien ne contient pas de virus, mais son contenu est trompeur',
        desc: 'Le lien en lui-même ne contient pas de code malveillant. CEPENDANT, le site vers lequel il mène diffuse des informations fausses ou non vérifiées. C\'est pour cette raison que nous le signalons comme dangereux.',
      });
    } else {
      reasons.push({
        title: '✅ Ce lien ne contient pas de piège visible',
        desc: 'Nous avons examiné le lien en détail. Il ne contient pas de code dangereux ni de technique d\'arnaque connue.',
      });
    }
  }

  // Content Legitimacy V2 (3 piliers : Véracité, Légalité, Intention)
  if (sources.content_legitimacy?.success && sources.content_legitimacy.findings?.length > 0) {
    const negativeMessages = {
      // Pilier 1 — Véracité
      scam_promises: { title: '🚨 Ce site promet de l\'argent facile', desc: 'Ce site fait des promesses irréalistes de gains ou de cadeaux gratuits. Personne ne donne d\'argent sur Internet.' },
      brand_impersonation: (f) => ({ title: `🚨 Ce site se fait passer pour ${f.brand?.charAt(0).toUpperCase()}${f.brand?.slice(1)}`, desc: `Ce site prétend être ${f.brand?.charAt(0).toUpperCase()}${f.brand?.slice(1)} mais ce n'est PAS le vrai. Le vrai site est ${f.real_domain}.` }),
      fake_testimonials: { title: '🚨 Faux témoignages sur ce site', desc: 'Ce site affiche de faux témoignages de gens qui auraient gagné de l\'argent. Ces témoignages sont inventés.' },
      counterfeit: { title: '🚨 Ce site vend probablement des contrefaçons', desc: 'Ce site propose des produits de grande marque à des prix anormalement bas. Ce sont très probablement des faux.' },
      // Pilier 2 — Légalité
      data_harvesting: { title: '🚨 Ce site récolte vos données sensibles', desc: 'Ce site demande des mots de passe, codes PIN ou informations Mobile Money. Ne donnez RIEN.' },
      no_legal_mentions: { title: '⚠️ Ce site n\'a pas de mentions légales', desc: 'La loi oblige tout site sérieux à afficher ses mentions légales. Ce site ne le fait pas.' },
      no_contact: { title: '⚠️ Impossible de contacter ce site', desc: 'Ce site ne donne aucun moyen de contacter les responsables.' },
      no_legal_entity: { title: '⚠️ Aucune entreprise identifiée', desc: 'Ce site ne mentionne aucune entreprise enregistrée.' },
      hidden_registrant: { title: '⚠️ Le propriétaire se cache', desc: 'Le propriétaire de ce site cache son identité.' },
      new_domain_scam: { title: '🚨 Site récent + contenu d\'arnaque', desc: 'Ce site vient d\'être créé et fait déjà des promesses d\'argent. Profil type d\'arnaque.' },
      inaccessible: { title: '⚠️ Ce site n\'est pas accessible', desc: 'Nous n\'avons pas pu ouvrir ce site. Il a peut-être été fermé.' },
      // Pilier 3 — Intention
      urgency_tactics: { title: '⚠️ Ce site vous manipule par l\'urgence', desc: 'Ce site utilise "dernière chance", "urgent", "votre compte sera fermé" pour vous empêcher de réfléchir.' },
      form_external: (f) => ({ title: '🚨 Vos données sont envoyées ailleurs', desc: `Quand vous remplissez un formulaire, vos informations partent vers ${f.external_host} — pas vers le site affiché.` }),
      title_mismatch: { title: '🚨 Le titre de la page est trompeur', desc: 'Le titre de cette page imite un site officiel mais l\'adresse ne correspond pas.' },
      hidden_redirect: { title: '⚠️ Ce site vous redirige en secret', desc: 'Cette page vous envoie automatiquement vers un autre site sans vous prévenir.' },
    };
    const positiveMessages = {
      legal_mentions: { title: '✅ Ce site a des mentions légales', desc: 'Ce site affiche ses conditions d\'utilisation et mentions légales, comme la loi l\'exige.' },
      contact_info: { title: '✅ Coordonnées de contact présentes', desc: 'Ce site fournit un moyen de le contacter (téléphone, email ou adresse).' },
      legal_entity: { title: '✅ Entreprise identifiable', desc: 'Ce site mentionne une entreprise enregistrée, signe de sérieux.' },
    };

    for (const finding of sources.content_legitimacy.findings) {
      if (finding.positive && !isDangerous) {
        const msg = positiveMessages[finding.type];
        if (msg) reasons.push({ title: msg.title, desc: msg.desc });
      } else if (!finding.positive && verdict !== 'safe') {
        const entry = negativeMessages[finding.type];
        if (typeof entry === 'function') {
          const msg = entry(finding);
          reasons.push({ title: msg.title, desc: msg.desc });
        } else if (entry) {
          reasons.push({ title: entry.title, desc: entry.desc });
        }
      }
    }
  }

  // Claim Verification (vérification factuelle des affirmations)
  if (sources.claim_verification?.success && sources.claim_verification.findings?.length > 0) {
    for (const finding of sources.claim_verification.findings) {
      if (finding.type === 'claim_verified' && !isDangerous) {
        reasons.push({ title: finding.title, desc: finding.detail });
      } else if (finding.type === 'claim_referenced' && !isDangerous) {
        reasons.push({ title: finding.title, desc: finding.detail });
      } else if (finding.type === 'claim_unverified') {
        reasons.push({ title: finding.title, desc: finding.detail });
      } else if (finding.type === 'inaccessible') {
        reasons.push({ title: `⚠️ ${finding.title}`, desc: finding.detail });
      }
    }
  }

  // ═══════════════════════════════════════════════════════
  // Si une attaque est CONFIRMÉE dans l'URL, les résultats
  // "safe" des autres services n'ont aucune valeur.
  // On ne montre que les signaux négatifs supplémentaires.
  // ═══════════════════════════════════════════════════════

  // VirusTotal
  if (sources.virustotal?.success && sources.virustotal.stats) {
    const vt = sources.virustotal;
    if (vt.stats.malicious_count > 0) {
      reasons.push({
        title: '🚨 Ce lien est connu comme dangereux',
        desc: 'Plusieurs experts en sécurité dans le monde ont déjà signalé ce lien. Il peut voler vos données ou votre argent.',
      });
    } else if (!vt.skipped && !isDangerous) {
      // Seulement affiché si AUCUNE attaque n'est détectée
      reasons.push({
        title: '✅ Ce lien n\'a jamais été signalé',
        desc: 'Nous avons consulté des experts en sécurité à travers le monde. Personne n\'a signalé ce lien comme dangereux.',
      });
    }
  }

  // Google Safe Browsing
  if (sources.gsb?.success && !sources.gsb.skipped) {
    if (sources.gsb.is_blacklisted) {
      reasons.push({
        title: '🚨 Ce site est sur une liste noire',
        desc: 'Ce site a déjà été signalé par d\'autres personnes comme une arnaque. N\'y entrez pas vos informations.',
      });
    } else if (!isDangerous) {
      reasons.push({
        title: '✅ Ce site n\'est pas sur les listes noires',
        desc: 'Personne n\'a encore signalé ce site comme une arnaque.',
      });
    }
  }

  // WHOIS / Domaine
  if (sources.whois?.success) {
    const w = sources.whois;
    const years = Math.floor(w.domain_age_days / 365);
    if (w.domain_age_days >= 0 && w.domain_age_days < 30) {
      reasons.push({
        title: '⚠️ Ce site vient d\'être créé',
        desc: `Ce site n'existe que depuis ${w.domain_age_days} jours. Les arnaqueurs créent souvent de nouveaux sites pour piéger les gens, puis disparaissent.`,
      });
    } else if (w.domain_age_days >= 0 && w.domain_age_days < 90) {
      reasons.push({
        title: '⚠️ Ce site est encore tout jeune',
        desc: 'Ce site a moins de 3 mois. Les vrais sites comme les banques ou les boutiques connues existent depuis des années.',
      });
    } else if (w.domain_age_days > 365 && !isDangerous) {
      // Ne PAS dire "ce site est ancien" si le lien contient une attaque
      reasons.push({
        title: '✅ Ce site existe depuis longtemps',
        desc: `Ce site a ${years} an${years > 1 ? 's' : ''}. C'est comme un magasin qui est dans le quartier depuis longtemps — c'est plutôt bon signe.`,
      });
    }
  }

  // URLScan
  if (sources.urlscan?.success && !sources.urlscan.skipped) {
    const us = sources.urlscan;
    if (us.is_malicious) {
      reasons.push({
        title: '🚨 Ce site essaie de vous piéger',
        desc: 'En ouvrant ce site, nous avons vu qu\'il essaie de récupérer vos informations personnelles. C\'est une arnaque.',
      });
    }
    if (us.redirections_count > 0) {
      reasons.push({
        title: '⚠️ Ce lien vous emmène sur un autre site',
        desc: 'Quand vous cliquez sur ce lien, il vous envoie secrètement vers une autre page. C\'est souvent utilisé pour tromper les gens.',
      });
    }
  }

  // ML
  if (sources.ml?.success && sources.ml.score != null) {
    if (sources.ml.score >= 60) {
      reasons.push({
        title: '⚠️ Ce lien ressemble à une arnaque connue',
        desc: 'Ce lien a les mêmes caractéristiques que des arnaques déjà vues. Soyez très prudent.',
      });
    } else if (!isDangerous) {
      reasons.push({
        title: '✅ Ce lien ne ressemble pas à une arnaque',
        desc: 'Ce lien ne présente pas les signes habituels des arnaques que nous connaissons.',
      });
    }
  }

  return reasons.length > 0 ? reasons : [{ title: 'Vérification terminée', desc: 'Nous avons vérifié ce lien pour vous.' }];
}

/* ═══════════════════════════════════════════════════
   Conseils SPÉCIFIQUES selon le type de danger
   ═══════════════════════════════════════════════════ */
function buildAdvice(sources, verdict, type) {
  // ── LIEN DANGEREUX ──
  if (verdict === 'danger') {
    const findings = sources?.url_pattern?.findings || [];
    const findingTypes = findings.map(f => f.type);
    const steps = [];

    // XSS détecté
    if (findingTypes.includes('xss')) {
      steps.push({
        title: '🛑 Ce lien vole vos informations',
        items: [
          'Ne cliquez JAMAIS sur ce lien et ne l\'ouvrez pas.',
          'Si vous avez déjà cliqué dessus, changez immédiatement vos mots de passe (email, banque, réseaux sociaux).',
          'Si quelqu\'un vous a envoyé ce lien, cette personne essaie peut-être de vous pirater.',
          'Supprimez le message contenant ce lien.',
        ],
      });
    }

    // SQL Injection détecté
    if (findingTypes.includes('sqli')) {
      steps.push({
        title: '🛑 Ce lien attaque un site internet',
        items: [
          'Ce lien essaie de pirater la base de données d\'un site.',
          'Ne le visitez pas — vous pourriez être complice sans le savoir.',
          'Si quelqu\'un vous a envoyé ce lien, signalez-le.',
        ],
      });
    }

    // Command Injection
    if (findingTypes.includes('command_injection')) {
      steps.push({
        title: '🛑 Ce lien prend le contrôle de votre appareil',
        items: [
          'Ce lien peut prendre le contrôle de votre téléphone ou ordinateur.',
          'Ne l\'ouvrez sous aucun prétexte.',
          'Si vous l\'avez déjà ouvert, faites vérifier votre appareil par un spécialiste.',
        ],
      });
    }

    // Typosquatting
    const typo = findings.find(f => f.type === 'typosquatting');
    if (typo) {
      const brand = typo.brand ? typo.brand.charAt(0).toUpperCase() + typo.brand.slice(1) : 'ce service';
      steps.push({
        title: `🛑 Ce n'est PAS le vrai ${brand}`,
        items: [
          `Ce site se fait passer pour ${brand} pour voler votre compte.`,
          `Le vrai site est ${typo.brand}.com — tapez-le vous-même dans votre navigateur.`,
          `Si vous avez entré votre mot de passe ici, allez IMMÉDIATEMENT sur le vrai ${brand} et changez votre mot de passe.`,
          'Vérifiez aussi votre compte bancaire si vous avez donné des informations de paiement.',
        ],
      });
    }

    // Path Traversal
    if (findingTypes.includes('path_traversal')) {
      steps.push({
        title: '🛑 Ce lien fouille dans des fichiers secrets',
        items: [
          'Ce lien tente d\'accéder à des fichiers confidentiels.',
          'Ne le visitez pas.',
          'Si vous êtes le propriétaire du site ciblé, quelqu\'un essaie de vous pirater.',
        ],
      });
    }

    // VT blacklisted
    if (sources?.virustotal?.stats?.malicious_count > 0) {
      steps.push({
        title: '🛑 Ce lien est fichié comme dangereux',
        items: [
          'Ce lien est connu dans le monde entier comme étant dangereux.',
          'Il a déjà été utilisé pour voler des données ou de l\'argent.',
          'Ne l\'ouvrez pas et supprimez-le.',
        ],
      });
    }

    // GSB blacklisted
    if (sources?.gsb?.is_blacklisted) {
      steps.push({
        title: '🛑 Ce site est bloqué pour arnaque',
        items: [
          'Ce site a été signalé comme une arnaque par de nombreuses personnes.',
          'Votre navigateur le bloquera normalement, mais ne prenez aucun risque.',
        ],
      });
    }

    // Content Legitimacy — promesses d'arnaque
    if (sources?.content_legitimacy?.has_scam_promises) {
      steps.push({
        title: '🛑 Ce site vous promet de l\'argent ou des cadeaux',
        items: [
          'Personne ne donne de l\'argent gratuitement sur Internet.',
          'N\'envoyez JAMAIS d\'argent, de crédit téléphonique ou de code Mobile Money pour "débloquer" un gain.',
          'Si on vous demande de payer pour recevoir un prix, c\'est 100% une arnaque.',
          'Les vrais concours ne demandent jamais de paiement.',
        ],
      });
    }

    // Content Legitimacy — collecte de données
    if (sources?.content_legitimacy?.has_data_harvesting) {
      steps.push({
        title: '🛑 Ce site veut récupérer vos informations',
        items: [
          'Ne tapez RIEN sur ce site : ni mot de passe, ni code PIN, ni numéro de carte.',
          'Ne donnez jamais votre code Mobile Money ou code de transfert.',
          'Si vous avez déjà donné vos informations, changez immédiatement vos mots de passe.',
          'Appelez votre opérateur (MTN, Moov, Orange) pour bloquer tout transfert suspect.',
        ],
      });
    }

    // Content Legitimacy — usurpation de marque (via contenu de la page)
    if (sources?.content_legitimacy?.has_brand_impersonation) {
      const brand = sources.content_legitimacy.impersonated_brand;
      const brandName = brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : 'un service connu';
      const finding = sources.content_legitimacy.findings?.find(f => f.type === 'brand_impersonation');
      const realDomain = finding?.real_domain || `${brand}.com`;
      steps.push({
        title: `🛑 Ce site prétend être ${brandName} — c'est FAUX`,
        items: [
          `Le VRAI site de ${brandName} est ${realDomain}. Tapez-le vous-même dans votre navigateur.`,
          `Si vous avez entré votre mot de passe, allez IMMÉDIATEMENT sur le vrai ${brandName} et changez-le.`,
          'Vérifiez vos comptes bancaires si vous avez donné des informations de paiement.',
          `Signalez ce faux site pour protéger les autres.`,
        ],
      });
    }

    // Content Legitimacy — faux témoignages
    if (sources?.content_legitimacy?.has_fake_testimonials) {
      steps.push({
        title: '🛑 Les témoignages sur ce site sont faux',
        items: [
          'Les personnes qui disent avoir gagné de l\'argent n\'existent pas.',
          'C\'est une technique pour vous mettre en confiance et vous arnaquer.',
          'Ne faites jamais confiance à des témoignages sur un site suspect.',
        ],
      });
    }

    // Content Legitimacy — contrefaçons
    if (sources?.content_legitimacy?.has_counterfeit) {
      steps.push({
        title: '🛑 Ce site vend probablement des produits faux',
        items: [
          'Les prix sont bien trop bas pour être vrais.',
          'Vous recevrez soit un produit de mauvaise qualité, soit rien du tout.',
          'Achetez uniquement sur les sites officiels des marques ou chez des revendeurs connus.',
        ],
      });
    }

    // Content Legitimacy — formulaire envoyé vers un autre site
    if (sources?.content_legitimacy?.has_form_to_external) {
      steps.push({
        title: '🛑 Vos données ne vont pas où vous croyez',
        items: [
          'Si vous remplissez un formulaire sur ce site, vos informations sont envoyées à un autre site.',
          'C\'est une technique classique pour voler vos données à votre insu.',
          'Ne remplissez AUCUN formulaire sur ce site.',
        ],
      });
    }

    // Content Legitimacy — pas de mentions légales + pas de contact
    if (sources?.content_legitimacy?.success && !sources.content_legitimacy.has_legal_mentions && !sources.content_legitimacy.has_contact_info) {
      steps.push({
        title: '🛑 Ce site n\'a aucune existence légale vérifiable',
        items: [
          'Ce site n\'affiche ni mentions légales, ni coordonnées de contact.',
          'La loi oblige tout site sérieux à afficher ces informations.',
          'En cas de problème, vous ne pourrez jamais contacter les responsables.',
        ],
      });
    }

    // ── CLAIM VERIFICATION : affirmations non vérifiées ──
    if (sources?.claim_verification?.unverified_claims > 0) {
      for (const claim of (sources.claim_verification.claims || [])) {
        if (claim.status !== 'unverified') continue;
        const entity = claim.entity;
        const finding = sources.claim_verification.findings?.find(f => f.entity === entity && f.type === 'claim_unverified');
        const officialDomain = finding?.official_domains?.[0] || `le site officiel de ${entity}`;

        if (claim.type === 'recruitment') {
          steps.push({
            title: `🛑 Ce recrutement au nom de ${entity} est probablement FAUX`,
            items: [
              `${entity} ne recrute PAS via ce site. Le site officiel est ${officialDomain}.`,
              `Allez vous-même sur ${officialDomain} et cherchez les offres d'emploi.`,
              'Si on vous demande de payer des "frais de dossier" pour postuler, c\'est une arnaque.',
              'Ne donnez jamais votre numéro de carte, code Mobile Money ou pièce d\'identité.',
              'Signalez ce lien pour protéger les autres.',
            ],
          });
        } else if (claim.type === 'promotion' || claim.type === 'lottery') {
          steps.push({
            title: `🛑 Cette promotion au nom de ${entity} n'existe PAS`,
            items: [
              `${entity} ne fait PAS cette offre. Le site officiel est ${officialDomain}.`,
              `Allez vous-même sur ${officialDomain} pour vérifier les vraies promotions.`,
              'Personne ne donne de cadeaux gratuits sur Internet sans contrepartie.',
              'Si on vous demande de "partager avec 10 amis" ou de payer pour recevoir un prix, c\'est faux.',
            ],
          });
        } else {
          steps.push({
            title: `🛑 Cette information au nom de ${entity} n'est PAS vérifiable`,
            items: [
              `Nous n'avons trouvé aucune confirmation de cette information sur le site officiel de ${entity} (${officialDomain}).`,
              `Vérifiez vous-même sur ${officialDomain} avant de faire confiance.`,
              'Ne partagez pas cette information sans vérification.',
            ],
          });
        }
      }
    }

    // Fallback — contenu frauduleux sans attaque technique
    if (steps.length === 0) {
      const hasUnverified = sources?.claim_verification?.unverified_claims > 0;
      const isFraudContent = sources?.content_legitimacy?.legitimacy === 'fraudulent';

      if (hasUnverified || isFraudContent) {
        steps.push({
          title: '🛑 Ce site diffuse des informations fausses ou non vérifiées',
          items: [
            'Ce lien ne contient pas de virus, mais le contenu du site est trompeur.',
            'Les informations affichées ne sont confirmées par aucune source officielle.',
            'Ne faites pas confiance à ce qui est écrit sur ce site.',
            'Vérifiez toujours les informations auprès des sites officiels avant d\'agir.',
            'Ne partagez pas ce lien à vos proches.',
          ],
        });
      } else {
        steps.push({
          title: '🛑 Ce lien est dangereux',
          items: [
            'Ne cliquez pas sur ce lien et ne l\'ouvrez pas.',
            'Si quelqu\'un vous l\'a envoyé, ne lui faites pas confiance.',
            'Supprimez le message contenant ce lien.',
          ],
        });
      }
    }

    return steps;
  }

  // ── LIEN SUSPECT ──
  if (verdict === 'suspect') {
    const findings = sources?.url_pattern?.findings || [];
    const findingTypes = findings.map(f => f.type);
    const steps = [];

    const typo = findings.find(f => f.type === 'typosquatting');
    if (typo) {
      const brand = typo.brand ? typo.brand.charAt(0).toUpperCase() + typo.brand.slice(1) : 'ce service';
      steps.push({
        title: `⚠️ Ce site ressemble à ${brand} mais ce n'est pas le vrai`,
        items: [
          `Vérifiez bien l'adresse : le vrai ${brand} c'est ${typo.brand}.com`,
          'N\'entrez pas votre mot de passe ni vos informations bancaires.',
          'En cas de doute, tapez vous-même l\'adresse dans votre navigateur.',
        ],
      });
    }

    if (findingTypes.includes('open_redirect')) {
      steps.push({
        title: '⚠️ Ce lien vous redirige en cachette',
        items: [
          'Ce lien vous emmène vers un autre site sans vous prévenir.',
          'Ne cliquez pas dessus si vous ne connaissez pas l\'expéditeur.',
        ],
      });
    }

    if (findingTypes.includes('suspicious_keywords')) {
      steps.push({
        title: '⚠️ Ce lien promet des choses trop belles pour être vraies',
        items: [
          'Les vraies entreprises ne donnent pas d\'argent ou de cadeaux par lien.',
          'Si on vous demande de payer pour recevoir un "prix", c\'est une arnaque.',
          'Ne donnez jamais votre numéro de Mobile Money ou de carte bancaire.',
        ],
      });
    }

    if (findingTypes.includes('suspicious_tld')) {
      steps.push({
        title: '⚠️ L\'adresse de ce site ne paraît pas fiable',
        items: [
          'Les sites sérieux utilisent des adresses comme .com, .org ou .fr.',
          'Ce site utilise une adresse inhabituelle souvent liée aux arnaques.',
          'Soyez très prudent avant de donner vos informations.',
        ],
      });
    }

    if (findingTypes.includes('no_https')) {
      steps.push({
        title: '⚠️ Vos informations ne sont pas protégées sur ce site',
        items: [
          'Ce site n\'a pas de cadenas 🔒 dans la barre d\'adresse.',
          'Ne tapez JAMAIS un mot de passe ou un numéro de carte sur ce site.',
        ],
      });
    }

    // Domaine récent
    if (sources?.whois?.domain_age_days < 90) {
      steps.push({
        title: '⚠️ Ce site vient d\'être créé',
        items: [
          'Ce site est très récent. Les arnaqueurs créent souvent des sites et disparaissent ensuite.',
          'Ne faites pas d\'achat et ne donnez pas vos informations personnelles.',
        ],
      });
    }

    if (steps.length === 0) {
      steps.push({
        title: '⚠️ Soyez prudent avec ce lien',
        items: [
          'Ce lien présente des signes suspects.',
          'Ne donnez pas vos informations personnelles (mot de passe, numéro de téléphone, carte bancaire).',
          'En cas de doute, demandez conseil à quelqu\'un de confiance.',
        ],
      });
    }

    return steps;
  }

  // ── LIEN SÛR ──
  const steps = [{
    title: '✅ Vous pouvez y aller tranquillement',
    items: [
      'Nous avons vérifié ce lien sous tous les angles. Il ne présente pas de danger.',
      'Vous pouvez cliquer dessus et utiliser le site normalement.',
      'Restez toujours vigilant : ne donnez jamais vos mots de passe si quelqu\'un vous les demande par message.',
    ],
  }];

  return steps;
}

function transformUrlResult(backend) {
  const verdictMap = { danger: 'danger', suspect: 'suspect', safe: 'safe' };
  const verdict = verdictMap[backend.verdict] || 'suspect';
  return {
    id: Date.now(),
    ref: backend.ref_id,
    type: 'url',
    label: backend.url,
    url: backend.url,
    score: backend.score,
    verdict,
    category: backend.score >= 70 ? 'Hameçonnage' : backend.score >= 40 ? 'Lien suspect' : 'Lien vérifié',
    analyzedAt: backend.analyzed_at,
    duration_ms: backend.duration_ms,
    reasons: buildReasons(backend.sources, verdict),
    advice: buildAdvice(backend.sources, verdict, 'url'),
    evidence: backend.evidence,
    timestamp: backend.timestamp,
    sources: backend.sources,
  };
}

function transformEmailResult(backend) {
  const parsed = backend.parsed_headers || {};
  const from = parsed.from || {};
  const contentAnalysis = backend.email_content_analysis || {};

  // ── Construire les raisons depuis les 3 axes (déjà en français clair côté backend) ──
  const reasons = [];

  // Axe 1 — Identité : qui envoie vraiment ?
  const identityFindings = contentAnalysis.identity || [];
  if (identityFindings.length > 0) {
    for (const f of identityFindings) {
      reasons.push({
        title: f.severity === 'critical' ? `🚨 ${f.title}` : `⚠️ ${f.title}`,
        desc: f.detail,
      });
    }
  }

  // Axe 2 — Contenu : arnaque détectée ?
  const contentFindings = contentAnalysis.content || [];
  if (contentFindings.length > 0) {
    for (const f of contentFindings) {
      reasons.push({
        title: f.severity === 'critical' ? `🚨 ${f.title}` : `⚠️ ${f.title}`,
        desc: f.detail,
        advice: f.advice,
      });
    }
  }

  // Axe 3 — Intention : liens piégés ?
  const intentFindings = contentAnalysis.intent || [];
  if (intentFindings.length > 0) {
    for (const f of intentFindings) {
      reasons.push({
        title: `🚨 ${f.title}`,
        desc: f.detail,
        advice: f.advice,
      });
    }
  }

  // Si rien de suspect trouvé — résultat propre
  const allFindings = [...identityFindings, ...contentFindings, ...intentFindings];
  if (allFindings.length === 0) {
    reasons.push({
      title: '✅ Aucun signal d\'arnaque détecté',
      desc: 'Nous avons examiné l\'expéditeur, le contenu et les liens de ce mail. Rien de suspect n\'a été trouvé.',
    });
  }

  // Auth — SPF/DKIM/DMARC en langage simple
  const spf = backend.sources?.spf;
  const dkim = backend.sources?.dkim;
  const dmarc = backend.sources?.dmarc;

  const authStatus = [];
  if (spf?.result === 'pass') authStatus.push('✅ Expéditeur autorisé');
  else if (spf?.result === 'fail') authStatus.push('❌ Expéditeur non autorisé');
  if (dkim?.result === 'pass') authStatus.push('✅ Mail non modifié');
  else if (dkim?.result === 'fail') authStatus.push('❌ Mail potentiellement modifié');
  if (dmarc?.policy === 'reject' || dmarc?.policy === 'quarantine') authStatus.push('✅ Domaine protégé');
  else if (dmarc?.result === 'fail') authStatus.push('❌ Domaine non vérifié');

  // Construire le conseil global
  const advice = [];
  if (contentFindings.length > 0) {
    for (const f of contentFindings) {
      if (f.advice) advice.push({ title: `💡 ${f.title}`, items: [f.advice] });
    }
  }
  if (intentFindings.length > 0) {
    for (const f of intentFindings) {
      if (f.advice) advice.push({ title: `💡 Liens dans ce mail`, items: [f.advice] });
    }
  }

  return {
    id: Date.now(),
    ref: backend.ref_id,
    type: 'email',
    label: parsed.subject || from.email || 'Email analysé',
    score: backend.score,
    verdict: backend.verdict,
    category: backend.score >= 70 ? 'Email frauduleux' : backend.score >= 40 ? 'Email suspect' : 'Email vérifié',
    analyzedAt: backend.analyzed_at,
    sender: {
      displayName: from.displayName || 'Inconnu',
      email: from.email || 'Non identifié',
      ip: parsed.originating_ip || null,
    },
    auth: {
      spf: spf?.result || 'N/A',
      dkim: dkim?.result || 'N/A',
      dmarc: dmarc?.policy || 'N/A',
      summary: authStatus.length > 0 ? authStatus : ['⚪ Vérification non disponible'],
    },
    contentWarnings: contentFindings.map(f => f.title),
    reasons,
    advice,
    links_found: contentAnalysis.links_found || 0,
    links_suspicious: contentAnalysis.links_suspicious || 0,
    scam_categories: contentAnalysis.scam_categories || [],
    indicators_of_compromise: parsed.indicators_of_compromise,
    evidence: backend.evidence,
    timestamp: backend.timestamp,
  };
}

function transformQrResult(backend) {
  if (backend.qr_decode) {
    // QR décodé + analyse URL faite
    const urlResult = transformUrlResult(backend);
    return {
      ...urlResult,
      type: 'qr',
      label: backend.qr_decode.extracted_url,
      category: urlResult.score >= 70 ? 'QR code dangereux' : urlResult.score >= 40 ? 'QR code suspect' : 'QR code vérifié',
      qr_decode: backend.qr_decode,
    };
  }
  // QR non décodé ou pas d'URL
  return {
    id: Date.now(),
    ref: backend.ref_id || `SEN-${Date.now().toString(36)}`,
    type: 'qr',
    label: 'QR code analysé',
    score: 0,
    verdict: backend.decoded ? 'suspect' : 'safe',
    category: 'QR code analysé',
    analyzedAt: backend.analyzed_at,
    reasons: [{ title: backend.error || 'QR code sans URL', desc: 'Le QR code ne contient pas de lien web. Aucune analyse de menace nécessaire.' }],
  };
}

// --- PDF (conservé côté client) ---

function escapePdfText(value) {
  return String(value || '').replace(/[()\\]/g, '\\$&').slice(0, 120);
}

// eslint-disable-next-line no-unused-vars
function buildPdf(data = {}) {
  const lines = [
    'SENTINELLE - Rapport forensique',
    `Reference: ${data.ref || 'N/A'}`,
    `Type: ${data.type || 'analyse'}`,
    `Score: ${data.score ?? 0}/100`,
    `Verdict: ${data.verdict || 'N/A'}`,
    `Element: ${data.url || data.label || 'N/A'}`,
    `Categorie: ${data.category || 'N/A'}`,
    `Date: ${data.analyzedAt || new Date().toISOString()}`,
  ];
  if (data.evidence?.hash) {
    lines.push(`SHA-256: ${data.evidence.hash.hash}`);
  }
  if (data.timestamp?.verified) {
    lines.push(`Timestamp: VERIFIE`);
  }
  const stream = `BT /F1 12 Tf 72 730 Td (${lines.map(escapePdfText).join(') Tj 0 -22 Td (')}) Tj ET`;
  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 5 0 R >> >> /Contents 4 0 R >>`,
    `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
  ];
  let pdf = '%PDF-1.4\n';
  const offsets = [0];
  objects.forEach((obj, i) => {
    offsets.push(pdf.length);
    pdf += `${i + 1} 0 obj\n${obj}\nendobj\n`;
  });
  const xref = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n${offsets.slice(1).map((o) => `${String(o).padStart(10, '0')} 00000 n `).join('\n')}\n`;
  pdf += `trailer << /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
  return pdf;
}

// --- Transformer : DB row → frontend item ---

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return "À l'instant";
  if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
  if (diff < 172800) return 'Hier';
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

function timeGroup(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diffDays = Math.floor((now - date) / 86400000);
  if (diffDays < 1) return 'recent';
  if (diffDays < 7) return 'week';
  return 'older';
}

function transformDbAnalysis(row) {
  const dbVerdictMap = { dangerous: 'danger', suspect: 'suspect', safe: 'safe' };
  const verdict = dbVerdictMap[(row.verdict || 'SAFE').toLowerCase()] || 'safe';
  const type = row.type || 'url';
  const details = typeof row.details === 'string' ? JSON.parse(row.details) : (row.details || {});

  const base = {
    id: row.id,
    ref: row.ref_id,
    type: type === 'qrcode' ? 'qr' : type,
    label: row.input || 'Analyse',
    score: row.score,
    verdict,
    analyzedAt: row.created_at,
    time: timeAgo(row.created_at),
    group: timeGroup(row.created_at),
  };

  // ── URL ou QR ──
  if (type === 'url' || type === 'qrcode') {
    const sources = details || {};
    const categoryMap = { danger: 'Hameconnage', suspect: 'Lien suspect', safe: 'Lien verifie' };
    return {
      ...base,
      url: row.input,
      category: type === 'qrcode'
        ? (verdict === 'danger' ? 'QR code dangereux' : verdict === 'suspect' ? 'QR code suspect' : 'QR code verifie')
        : (categoryMap[verdict] || 'Analyse'),
      reasons: buildReasons(sources),
      advice: buildAdvice(sources, verdict, type),
      sources,
    };
  }

  // ── Email ──
  if (type === 'email') {
    const parsed = details.parsed_headers || {};
    const contentAnalysis = details.email_content_analysis || {};
    const emailSources = details.sources || {};
    const from = parsed.from || {};
    const spf = emailSources.spf;
    const dkim = emailSources.dkim;
    const dmarc = emailSources.dmarc;

    // Reconstruire les raisons email
    const reasons = [];
    if (contentAnalysis.scam_categories?.length > 0) {
      const cats = contentAnalysis.scam_categories.map(c => c === 'phishing' ? 'vol de donnees' : c === 'momo' ? 'Mobile Money' : c === 'fake_job' ? 'faux emploi' : c).join(', ');
      reasons.push({ title: '\ud83d\udea8 Technique d\'arnaque reconnue', desc: `Ce message utilise des methodes connues pour tromper les gens (liees a : ${cats}).` });
    }
    if (contentAnalysis.links_suspicious > 0) {
      reasons.push({ title: '\ud83d\udea8 Liens dangereux caches', desc: 'Le message contient des liens qui peuvent voler vos informations si vous cliquez dessus.' });
    }
    const spfResult = spf?.result || parsed.authentication_results?.spf || null;
    const dkimResult = dkim?.result || parsed.authentication_results?.dkim || null;
    const dmarcResult = dmarc?.result || parsed.authentication_results?.dmarc || null;
    const authAllPass = spfResult === 'pass' && dkimResult === 'pass' && dmarcResult === 'pass';
    const authAnyFail = spfResult === 'fail' || dkimResult === 'fail' || dmarcResult === 'fail';
    if (authAllPass) {
      reasons.push({ title: '\u2705 Expediteur verifie', desc: 'L\'identite de l\'envoyeur a ete confirmee techniquement.' });
    } else if (authAnyFail) {
      reasons.push({ title: '\u26a0\ufe0f Fausse adresse d\'expedition', desc: 'Le systeme a detecte que l\'expediteur n\'est pas celui qu\'il pretend etre.' });
    }
    if (reasons.length === 0) {
      reasons.push(row.score < 40
        ? { title: '\u2705 Message d\'apparence normale', desc: 'Le contenu semble inoffensif.' }
        : { title: '\u26a0\ufe0f Contenu inhabituel', desc: 'Le message presente des elements inhabituels.' }
      );
    }

    const categoryMap = { danger: 'Email frauduleux', suspect: 'Email suspect', safe: 'Email verifie' };
    return {
      ...base,
      label: parsed.subject || from.email || row.input || 'Email analyse',
      category: categoryMap[verdict] || 'Analyse',
      sender: {
        displayName: from.displayName || 'Inconnu',
        email: from.email || 'Non identifie',
        ip: parsed.originating_ip || null,
      },
      auth: {
        spf: spfResult || 'N/A',
        dkim: dkimResult || 'N/A',
        dmarc: dmarcResult || 'N/A',
      },
      reasons,
      advice: [],
      contentWarnings: contentAnalysis.scam_categories || [],
      links_found: contentAnalysis.links_found || 0,
      links_suspicious: contentAnalysis.links_suspicious || 0,
      sources: emailSources,
    };
  }

  // Fallback
  return {
    ...base,
    category: 'Analyse',
    reasons: [{ title: 'Verification terminee', desc: 'Analyse effectuee.' }],
    advice: [],
    sources: details,
  };
}

// --- API publique ---

export const api = {
  /**
   * Analyse une URL via le backend réel.
   * POST /api/analyze/url
   */
  analyzeUrl: async (url) => {
    const resp = await http.post('/analyze/url', { url });
    return transformUrlResult(resp.data);
  },

  /**
   * Analyse un email via le backend réel.
   * POST /api/analyze/email
   */
  analyzeEmail: async (headers) => {
    const resp = await http.post('/analyze/email', { headers });
    return transformEmailResult(resp.data);
  },

  /**
   * Analyse un QR code via le backend réel.
   * POST /api/analyze/qrcode (multipart)
   */
  analyzeQrCode: async (imageFile) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    const resp = await http.post('/analyze/qrcode', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return transformQrResult(resp.data);
  },

  /**
   * Historique depuis la BDD via GET /api/history.
   * Retourne les analyses de l'utilisateur connecté.
   */
  getHistory: async () => {
    try {
      const token = localStorage.getItem('sentinelle_token');
      if (!token) return [];
      const resp = await http.get('/history', { params: { limit: 100 } });
      const analyses = resp.data.analyses || [];
      return analyses.map(transformDbAnalysis);
    } catch {
      return [];
    }
  },

  getReports: async () => {
    try {
      const token = localStorage.getItem('sentinelle_token');
      if (!token) return [];
      const resp = await http.get('/history', { params: { limit: 100 } });
      const analyses = resp.data.analyses || [];
      return analyses.map(a => ({ ...transformDbAnalysis(a), reportReady: true }));
    } catch { return []; }
  },

  getSignalements: async () => readLocalSignalements(),

  sendSignalement: async ({ analysis, country }) => {
    const entry = {
      id: `SIG-${Date.now()}`,
      ref: makeReference('SENT'),
      title: analysis?.url || analysis?.label || 'Analyse SENTINELLE',
      authority: country?.institution || 'Autorité compétente',
      status: 'Envoyé',
      date: "À l'instant",
      type: analysis?.category || 'Menace signalée',
      analysis,
      country,
    };
    writeLocalSignalements([entry, ...readLocalSignalements()].slice(0, 50));
    return entry;
  },

  downloadReportPdf: async (data) => {
    try {
      if (!data?.ref) throw new Error("Référence manquante");
      const resp = await http.get(`/report/${data.ref}`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rapport-sentinelle-${data.ref}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Erreur téléchargement PDF:", err);
      alert("Erreur lors de la génération du rapport PDF certifié.");
    }
  },

  /**
   * No-op — le backend sauvegarde déjà l'analyse en BDD.
   * Conservé pour compatibilité des appels existants.
   */
  saveToHistory: () => {},

  /**
   * Récupère les stats du profil depuis la BDD.
   */
  getStats: async () => {
    try {
      const token = localStorage.getItem('sentinelle_token');
      if (!token) return { analyses: 0, signalements: 0 };
      const resp = await http.get('/history/stats');
      return resp.data;
    } catch {
      return { analyses: 0, signalements: 0 };
    }
  },

  /**
   * Envoie un message au chatbot cybersécurité.
   */
  sendChatMessage: async (message, context, history) => {
    const resp = await http.post('/chat', { message, context, history });
    return resp.data;
  },
};

export default api;
